import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { AddButtonFooter } from "@/components/ui/add-button-footer";
import { UserData, UserStatus, UserRole } from "@/entities/users/types";
import { APILookupValue } from "@/services/api/types";
import {
  useRoles,
  useUpdateUser,
  useResetUsersInfinite,
  useDepartments,
  useAreaOfResponsibility,
  useAreaOfResponsibilityUpsert
} from "@/entities/users/hooks";
import { useEntitySearch } from "@/services/hooks/entities/useEntitySearch";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import UserAvatar from "@/components/controls/UserAvatar";
import { Close } from "@dreamhub/designsystem/icons";
import Tabs from "@/components/controls/Tabs";
import DeactivateUserModal from "@/pages/settings/components/DeactivateUserModal";
import CreateNewOptionModal from "./CreateNewOptionModal";
import { TransferData } from "./hooks/types";
import { useUserDeactivation } from "./hooks/useUserDeactivation";
import { getTransferErrorTranslationKey } from "./helpers";
import { IconButton } from "@/components/ui/icon-button";
import UnsavedChangesInDrawerModal from "@/pages/settings/components/UnsavedChangesInDrawerModal";

const TRANSLATION_KEY = "settings.userManagement.editUserModal";

interface EditUserDrawerProps {
  isOpen: boolean;
  onClose: (userWasUpdated?: boolean) => void;
  userData: UserData | null;
  isManagedByIdentityProvider?: boolean;
}

const EditUserDrawer: React.FC<EditUserDrawerProps> = ({
  isOpen,
  onClose,
  userData,
  isManagedByIdentityProvider = false
}) => {
  const { t } = useTranslation();
  const { data: rolesData } = useRoles();
  const { data: departments } = useDepartments();
  const { data: areaOfResponsibility, refetch: refetchAreaOfResponsibility } =
    useAreaOfResponsibility();
  const areaOfResponsibilityUpsert = useAreaOfResponsibilityUpsert(
    (response: APILookupValue) => {
      setAreaOptions((prev: APILookupValue[]) => [...prev, response]);
      setFilteredAreaOptions((prev: APILookupValue[]) => [...prev, response]);
      refetchAreaOfResponsibility();

      if (formSetValue) {
        formSetValue("areaOfResponsibility", response.id);
        handleFormChange();
      }
    }
  );
  const updateUserMutation = useUpdateUser();
  const { deactivateUsers } = useUserDeactivation();
  const resetUsersInfinite = useResetUsersInfinite();
  const formRef = useRef<HTMLFormElement>(null);
  const [formSetValue, setFormSetValue] = useState<
    ((field: string, value: unknown) => void) | null
  >(null);

  const [isFormValid, setIsFormValid] = useState(!!userData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [transferData, setTransferData] = useState<TransferData | null>(null);
  const [areaOptions, setAreaOptions] = useState<APILookupValue[]>([]);
  const [filteredAreaOptions, setFilteredAreaOptions] = useState<
    APILookupValue[]
  >([]);
  const [areaSearchTerm, setAreaSearchTerm] = useState("");
  const [userStatus, setUserStatus] = useState<UserStatus>(
    userData?.status || UserStatus.ACTIVE
  );
  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");

  const { options: allManagerOptions, isLoading: managersLoading } =
    useEntitySearch({
      entityType: EntityType.USER,
      filters: {
        status: [UserStatus.ACTIVE]
      }
    });

  useEffect(() => {
    const areas = areaOfResponsibility?.area_of_responsibility;

    if (areas && Array.isArray(areas)) {
      setAreaOptions(areas);
      setFilteredAreaOptions(areas);
    } else {
      setAreaOptions([]);
      setFilteredAreaOptions([]);
    }
  }, [areaOfResponsibility]);

  useEffect(() => {
    if (isOpen && userData) {
      setUserStatus(userData.status || UserStatus.ACTIVE);
      setHasUnsavedChanges(false);
      setAreaSearchTerm("");
      setFilteredAreaOptions(areaOptions);
    } else if (!isOpen) {
      setHasUnsavedChanges(false);
      setAreaSearchTerm("");
      setFilteredAreaOptions(areaOptions);
      if (userData) {
        setUserStatus(userData.status || UserStatus.ACTIVE);
      }
    }
  }, [isOpen, userData, areaOptions]);

  const roleOptions = useMemo(() => {
    if (!rolesData?.roles) return [];
    return rolesData.roles.map((role: UserRole) => ({
      id: role.id,
      displayName: role.name,
      subtitle: role.description
    }));
  }, [rolesData]);

  const availableManagers = allManagerOptions;

  const functionOptions = useMemo(() => {
    if (!departments?.departments || !Array.isArray(departments.departments))
      return [];

    return departments.departments.map((dept: APILookupValue) => ({
      id: dept.id,
      displayName: dept.name
    }));
  }, [departments]);

  const handleAreaSearch = (searchTerm: string) => {
    setAreaSearchTerm(searchTerm);
    setHasUnsavedChanges(true);

    if (searchTerm.trim() === "") {
      setFilteredAreaOptions(areaOptions);
      return;
    }

    const filtered = areaOptions.filter((option: APILookupValue) =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAreaOptions(filtered);
  };

  const handleCreateNewOption = async (optionName: string) => {
    try {
      await areaOfResponsibilityUpsert.mutateAsync({
        name: optionName
      });
    } catch (error) {
      console.error("Failed to create area of responsibility:", error);
    }
  };

  const hasActualChanges = (data: Partial<UserData>) => {
    if (!userData) return false;

    return (
      data.firstName !== userData.firstName ||
      data.lastName !== userData.lastName ||
      data.email !== userData.email ||
      data.role !== userData.role ||
      (data.title || null) !== (userData.jobTitle || null) ||
      (data.reportsTo || null) !== (userData.managerId || null) ||
      (data.areaOfResponsibility || null) !==
        (userData.areaOfResponsibility || null) ||
      (data.department || null) !== (userData.department || null) ||
      userStatus !== userData.status
    );
  };

  const handleSubmit = async (data: Partial<UserData>) => {
    if (!userData) return;

    if (!hasActualChanges(data)) {
      setHasUnsavedChanges(false);
      onClose();
      return;
    }
    try {
      const updateData: Partial<UserData> = {
        firstName: data.firstName,
        lastName: data.lastName,
        displayName: `${data.firstName} ${data.lastName}`,
        email: data.email,
        role: isManagedByIdentityProvider ? undefined : data.role,
        jobTitle: data.title || undefined,
        managerId: data.reportsTo || undefined,
        areaOfResponsibility: data.areaOfResponsibility || undefined,
        department: data.department || undefined,
        status: isManagedByIdentityProvider ? undefined : userStatus
      };
      if (
        !isManagedByIdentityProvider &&
        userStatus === UserStatus.INACTIVE &&
        userData.status !== UserStatus.INACTIVE
      ) {
        await deactivateUsers([userData.id], transferData);
      } else {
        await updateUserMutation.mutateAsync({
          pathParams: { userId: userData.id },
          data: updateData
        });
      }

      resetUsersInfinite();
      setHasUnsavedChanges(false);
      setTransferData(null);
      onClose(true);
    } catch (error) {
      console.error("Failed to update user:", error);
      const transferErrorKey = getTransferErrorTranslationKey(error);
      toast.error(
        transferErrorKey ? t(transferErrorKey) : "Failed to update user"
      );
    }
  };

  const handleSubmitButton = () => {
    formRef.current?.requestSubmit();
  };

  const handleCancel = () => {
    const formData = new FormData(formRef.current!);
    const data: Partial<UserData> = {
      firstName: formData.get("firstName") as string,
      lastName: formData.get("lastName") as string,
      email: formData.get("email") as string,
      role: parseInt(formData.get("role") as string) || 0,
      jobTitle: formData.get("title") as string,
      managerId: formData.get("reportsTo") as string,
      areaOfResponsibility: formData.get("areaOfResponsibility") as string,
      department: parseInt(formData.get("department") as string) || undefined
    };

    if (hasUnsavedChanges && hasActualChanges(data)) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  };

  const handleDiscardChanges = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    onClose();
  };

  const handleStatusToggle = (status: UserStatus) => {
    if (
      status === UserStatus.INACTIVE &&
      userData?.status === UserStatus.ACTIVE
    ) {
      setUserStatus(UserStatus.INACTIVE);
      setHasUnsavedChanges(true);
      setShowDeactivateModal(true);
    } else {
      setUserStatus(status);
      setHasUnsavedChanges(true);
    }
  };

  const handleDeactivateUser = (deactivateTransferData: TransferData) => {
    // The actual deactivation will happen when the form is submitted
    setTransferData(deactivateTransferData);
    setHasUnsavedChanges(true);
    setShowDeactivateModal(false);
  };

  const handleCloseDeactivateModal = () => {
    setShowDeactivateModal(false);
    setUserStatus(userData?.status || UserStatus.ACTIVE);
    setTransferData(null);
  };

  const handleFormChange = () => {
    setHasUnsavedChanges(true);
  };

  if (!userData) return null;

  const sections: FormSectionProps[] = [
    {
      id: "userDetails",
      halfFieldClassName: "mb-1",
      fields: [
        {
          name: "firstName",
          label: t(`${TRANSLATION_KEY}.fields.name`),
          type: "text",
          required: true,
          layout: "half",
          defaultValue: userData.firstName,
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_KEY}.placeholders.firstName`),
            onChange: handleFormChange
          }
        },
        {
          name: "lastName",
          label: "",
          type: "text",
          required: Boolean(userData.lastName),
          layout: "half",
          fieldClassName: "mt-8",
          defaultValue: userData.lastName,
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_KEY}.placeholders.lastName`),
            onChange: handleFormChange
          }
        },
        {
          name: "email",
          label: t(`${TRANSLATION_KEY}.fields.email`),
          type: "email",
          required: true,
          layout: "flex",
          defaultValue: userData.email,
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_KEY}.placeholders.email`),
            onChange: handleFormChange
          }
        },
        {
          name: "title",
          label: t(`${TRANSLATION_KEY}.fields.title`),
          type: "text",
          required: false,
          layout: "flex",
          defaultValue: userData.jobTitle || "",
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_KEY}.placeholders.title`),
            onChange: handleFormChange
          }
        },
        {
          name: "role",
          label: t(`${TRANSLATION_KEY}.fields.role`),
          type: "singleSelect",
          required: true,
          layout: "flex",
          defaultValue: userData.role,
          disabled: isManagedByIdentityProvider,
          hintText: isManagedByIdentityProvider
            ? t(`${TRANSLATION_KEY}.managedByIdentityProvider`)
            : undefined,
          fieldActionProps: {
            options: roleOptions,
            placeholder: t(`${TRANSLATION_KEY}.placeholders.role`),
            showSearch: true,
            variant: "form",
            showSelectedItemSubtitle: false,
            onSelection: handleFormChange,
            dropdownDirection: "bottom"
          }
        },
        {
          name: "department",
          label: t(`${TRANSLATION_KEY}.fields.department`),
          type: "singleSelect",
          required: false,
          layout: "flex",
          defaultValue: userData.department || "",
          fieldActionProps: {
            options: functionOptions,
            placeholder: t(`${TRANSLATION_KEY}.placeholders.department`),
            showSearch: true,
            variant: "form",
            onSelection: handleFormChange,
            dropdownDirection: "bottom"
          }
        },
        {
          name: "reportsTo",
          label: t(`${TRANSLATION_KEY}.fields.reportsTo`),
          type: "singleSelect",
          required: false,
          layout: "flex",
          defaultValue: userData.managerId || "",
          fieldActionProps: {
            options: availableManagers,
            placeholder: t(`${TRANSLATION_KEY}.placeholders.manager`),
            showSearch: true,
            variant: "form",
            isLoading: managersLoading,
            onSelection: handleFormChange,
            dropdownDirection: "bottom",
            minItemHeight: "46px",
            renderIcon: (userId: string | number) => {
              const option = availableManagers.find((opt) => opt.id === userId);
              const user = option?.userData as UserData;
              return user ? (
                <UserAvatar userData={user} size="M" />
              ) : (
                <div className="size-5" />
              );
            }
          }
        },
        {
          name: "areaOfResponsibility",
          label: t(`${TRANSLATION_KEY}.fields.areaOfResponsibility`),
          type: "singleSelect",
          required: false,
          layout: "flex",
          defaultValue: userData.areaOfResponsibility || "",
          fieldActionProps: {
            options: filteredAreaOptions,
            placeholder: t(
              `${TRANSLATION_KEY}.placeholders.areaOfResponsibility`
            ),
            showSearch: true,
            variant: "form",
            onSearch: handleAreaSearch,
            initialSearchTerm: areaSearchTerm,
            onSelection: handleFormChange,
            dropdownDirection: "bottom",
            popOverFooter:
              filteredAreaOptions.length === 0 || areaOptions.length === 0 ? (
                <AddButtonFooter
                  buttonText={t(
                    "settings.userManagement.addNewUserModal.createNewOption"
                  )}
                  onClick={() => {
                    setNewOptionName(areaSearchTerm || "");
                    setShowCreateOptionModal(true);
                  }}
                  variant="iconNoBg"
                  buttonClassName="text-cta hover:bg-surface-elevation2 hover:!text-cta disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!areaSearchTerm.trim()}
                />
              ) : undefined
          }
        }
      ]
    }
  ];

  return (
    <>
      <Drawer
        open={isOpen}
        direction="right"
        fixed
        modal={false}
        disablePreventScroll={false}
        noBodyStyles={true}
        dismissible={false}
      >
        <AppDrawerContent isSettingsDrawer>
          <DrawerHeader>
            <DrawerTitle>
              <UserAvatar userData={userData} size="XL" />
              <Text variant="lgSubtitle">{userData?.displayName}</Text>
            </DrawerTitle>
            <IconButton
              data-test="close-user-drawer"
              onClick={handleCancel}
              variant="iconNoBg"
              size="sm"
            >
              <Close />
            </IconButton>
          </DrawerHeader>

          <div className="p-5 w-full overflow-y-scroll">
            <FormNew
              sections={sections}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={(formMethods) => {
                setFormSetValue(() => formMethods.setValue);
              }}
            />

            <div className="mt-8 border-t-[0.5px] border-border-border1 pt-8">
              <div className="w-fit">
                <Tabs
                  options={{
                    [UserStatus.ACTIVE]: t(
                      `${TRANSLATION_KEY}.statusOptions.active`
                    ),
                    [UserStatus.INACTIVE]: t(
                      `${TRANSLATION_KEY}.statusOptions.inactive`
                    )
                  }}
                  value={userStatus.toString()}
                  valueSelected={(value) =>
                    handleStatusToggle(parseInt(value) as UserStatus)
                  }
                  size="lg"
                  isPositiveNegative
                  disabled={isManagedByIdentityProvider}
                  readOnly={isManagedByIdentityProvider}
                />
              </div>
            </div>
          </div>

          <DrawerFooter>
            <div className="flex justify-end gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
              <Button
                data-test="cancel-button"
                variant="secondaryNoBg"
                onClick={handleCancel}
              >
                {t("cancel")}
              </Button>
              <Button
                data-test="submit-button"
                disabled={!isFormValid}
                onClick={handleSubmitButton}
              >
                {t(`${TRANSLATION_KEY}.save`)}
              </Button>
            </div>
          </DrawerFooter>
        </AppDrawerContent>
      </Drawer>
      <UnsavedChangesInDrawerModal
        isOpen={showUnsavedModal}
        setIsOpen={setShowUnsavedModal}
        handleSubmit={() => {
          setShowUnsavedModal(false);
          handleSubmitButton();
        }}
        handleCancel={handleDiscardChanges}
      />
      {userData && (
        <DeactivateUserModal
          isOpen={showDeactivateModal}
          onClose={handleCloseDeactivateModal}
          userData={userData}
          onDeactivate={handleDeactivateUser}
        />
      )}

      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => setShowCreateOptionModal(false)}
        onCreateOption={handleCreateNewOption}
        optionName={newOptionName}
      />
    </>
  );
};

export default EditUserDrawer;
